import React, { FC, useCallback, useEffect, useState } from 'react';
import { InputNumber, Select } from 'antd';
import { SalaryRange } from '../../models/UserModels';
import { useCachedData } from '../../connection/CacheContext';

const { Option } = Select;

type Props = {
  value?: SalaryRange;
  onChange?: (value?: SalaryRange) => void;
  single?: boolean;
};

export const SalaryInputRange: FC<Props> = ({ value, onChange, single }) => {
  const [allCurrency, setAllCurrency] = useState<any[]>([]);
  const [type, setType] = useState<string>(value?.type ?? 'Month');
  const [currency, setCurrency] = useState<string>(value?.currency ?? '1');
  const [amount, setAmount] = useState<number | undefined>(value?.amount);
  const [fromAmount, setFromAmount] = useState<number | undefined>(value?.fromAmount);

  const { mainType, masterdata } = useCachedData();

  const emit = useCallback(
    (next: Partial<SalaryRange>) => {
      const payload: SalaryRange = {
        type,
        currency,
        amount: amount ?? 0,
        fromAmount: fromAmount ?? 0,
        ...next,
      };

      if (single) {
        payload.fromAmount = 0;
      }

      const hasValue = (payload.amount ?? 0) > 0 || (!single && (payload.fromAmount ?? 0) > 0);

      onChange?.(hasValue ? payload : undefined);
    },
    [type, currency, amount, fromAmount, single, onChange],
  );

  const onTypeChange = useCallback(
    (val: string) => {
      setType(val);

      if (val === 'Hour' || single) {
        setFromAmount(undefined);
        emit({ type: val, fromAmount: 0 });
      } else {
        emit({ type: val });
      }
    },
    [emit, single],
  );

  const onCurrencyChange = useCallback(
    (val: string) => {
      setCurrency(val);
      emit({ currency: val });
    },
    [emit],
  );

  const onMinChange = useCallback(
    (val: number | null) => {
      const cleaned = val ?? undefined;

      // Single mode or Hour → always single amount
      if (single || type === 'Hour') {
        setAmount(cleaned);
        emit({ amount: cleaned ?? 0, fromAmount: 0 });
      } else {
        setFromAmount(cleaned);
        emit({ fromAmount: cleaned ?? 0 });
      }
    },
    [type, single, emit],
  );

  const onMaxChange = useCallback(
    (val: number | null) => {
      const cleaned = val ?? undefined;
      setAmount(cleaned);
      emit({ amount: cleaned ?? 0 });
    },
    [emit],
  );

  useEffect(() => {
    const currencyId = mainType['Currency'];
    if (currencyId) {
      const data: any[] = masterdata[currencyId] || [];
      const options = data.map((d) => ({ label: d.value, value: d.id }));
      setAllCurrency(options);
    }
  }, [mainType, masterdata]);

  useEffect(() => {
    if (!value) return;
    setType(value.type ?? 'Month');
    setCurrency(value.currency ?? '1');
    setAmount(value.amount);
    setFromAmount(value.fromAmount);
  }, [value]);

  const currencyAfter = (
    <Select
      value={currency}
      onChange={onCurrencyChange}
      options={allCurrency}
      showSearch
      style={{ width: 90 }}
      filterOption={(input, option) =>
        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
      }
    />
  );

  const showRange = !single && type !== 'Hour';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
      <div className="flex flex-col">
        <label className="mb-2 text-base font-semibold text-gray-700">Salary Type</label>
        <Select value={type} onChange={onTypeChange} className="w-full">
          <Option value="Month">Per Month</Option>
          <Option value="Day">Per Day</Option>
          <Option value="Hour">Per Hour</Option>
          <Option value="Year">Per Year</Option>
        </Select>
      </div>

      <div className="flex flex-col h-12">
        <label className="mb-2 text-base font-semibold text-gray-700">
          {single || type === 'Hour' ? 'Salary' : 'Min Salary'}
        </label>
        <InputNumber
          className="w-full h-12"
          min={0}
          controls={false}
          placeholder={single ? 'Enter salary' : type === 'Hour' ? 'Amount' : 'Minimum salary'}
          value={single || type === 'Hour' ? amount : fromAmount}
          onChange={onMinChange}
          addonAfter={currencyAfter}
        />
      </div>

      {showRange && (
        <div className="flex flex-col">
          <label className="mb-2 text-base font-semibold text-gray-700">Max Salary</label>
          <InputNumber
            className="w-full h-12"
            min={0}
            controls={false}
            placeholder="Maximum salary"
            value={(amount ?? 0) > 0 ? amount : undefined}
            onChange={onMaxChange}
            addonAfter={currencyAfter}
          />
        </div>
      )}
    </div>
  );
};
